import { useCallback, useEffect, useState } from 'react';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import {
  isArcGisOAuthSessionValid,
  registerArcGisOAuthConfig,
  registerArcGisOAuthInfo,
} from '../arcgis-integration/arcGisPortal';

export function useArcGisAuthorization() {
  const group = useCurrentGroup();
  const [isArcGISAuthorized, setIsArcGISAuthorized] = useState(false);
  const [isCheckingArcGISAuthorization, setIsCheckingArcGISAuthorization] =
    useState(false);

  useEffect(() => {
    if (!group?.group_guid || !group.oauth_client_id) {
      setIsArcGISAuthorized(false);
      setIsCheckingArcGISAuthorization(false);
      return;
    }

    // Step 1: register ServerInfo + OAuthInfo so IdentityManager can parse URL on OAuth redirect-back.
    const portalUrl = registerArcGisOAuthConfig(group);
    if (!portalUrl) {
      setIsArcGISAuthorized(false);
      setIsCheckingArcGISAuthorization(false);
      return;
    }

    // Step 2: check if a valid session already exists (non-interactive).
    let isCancelled = false;
    setIsCheckingArcGISAuthorization(true);

    const checkArcGisSession = async () => {
      try {
        const isValid = await isArcGisOAuthSessionValid(group);
        if (!isCancelled) {
          setIsArcGISAuthorized(isValid);
        }
      } finally {
        if (!isCancelled) {
          setIsCheckingArcGISAuthorization(false);
        }
      }
    };

    checkArcGisSession();

    return () => {
      isCancelled = true;
    };
  }, [group]);

  const authorizeArcGIS = useCallback(async () => {
    if (!group?.group_guid || !group.oauth_client_id) {
      setIsArcGISAuthorized(false);
      return;
    }

    try {
      const success = await registerArcGisOAuthInfo(group);
      setIsArcGISAuthorized(success);
    } catch (error) {
      console.error(
        '[ArcGIS] authorizeArcGIS: failed to register ArcGIS OAuth info',
        error,
        group
      );
      setIsArcGISAuthorized(false);
      throw error;
    }
  }, [group]);

  return {
    isArcGISAuthorized,
    isCheckingArcGISAuthorization,
    authorizeArcGIS,
    setIsArcGISAuthorized,
  };
}
